/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useEffect, useRef, useState } from 'react';
import { type AddCertonaItemObjectType, type CertonaRecommendation } from '@/features/certona';
import { useDealDrawer } from '@/features/deals/hooks/useDealDrawer';
import { useContentStackPLPData } from '@/features/productListingPage/api/getContentStackProductListingPageData';
import { CMSPLPBodyModularHandler } from '../../../productListingPage/components/CMSModularHandler';
import { CMSShelfResultHeaderModularHandler } from '../../../shelf/components/ShelfDetails/CMSModularHandler';
import { View } from '@az/starc-ui';
import styles from './styles.module.scss';
import { Filter } from '@/components/Filter';
import { useSearchProductData } from '../../api/getSearchProductData';
import { useSearchPartTypes } from '../../api/getSearchPartTypes';
import useLatestCallback from 'use-latest-callback';
import { type HandleFilterType } from '@/types/filterTypes';
import { useDispatch } from '@/hooks/redux/useDispatch';
import { removeDataFromPage, setProductFilterEvents } from '@/actions/shelf/shelf';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import SearchResultsMiniCart from '../SearchResultsMiniCart';
import { CMSResultBottom } from '../../../shelf/components/ShelfDetails/CMSModularHandler/CMSResultBottom';
import SearchResultsCertonaMiniCart from '../SearchResultsCertonaMiniCart';
import ShelfSkeleton from '@/components/Skeletons/ShelfSkeleton';
import TwoColumnSkeleton from '@/components/Skeletons/TwoColumn';
import { useSelector } from 'react-redux';
import { type ReduxState } from '@/types';
import PartTypesView from '../PartTypesView';
import { useProductListView } from '@/features/shelf/context/useProductListView';
import { FilterMobileDrawer } from '@/components/Filter/FilterMobileDrawer';
import { useProductList } from '@/features/shelf/context/useProductList';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { useSearchAnalytics } from '../../lib/hooks/useSearchAnalytics';

type Props = {
  certonaRecommendations: CertonaRecommendation[];
  viewingPartTypes: boolean;
  setViewingPartTypes: React.Dispatch<React.SetStateAction<boolean>>;
  setAnalyticsCalled: (called: boolean) => void;
  analyticsCalled: boolean;
  analyticsEventType: string | undefined;
};

const CMSSearchResultsPage = ({
  certonaRecommendations,
  viewingPartTypes,
  setViewingPartTypes,
  setAnalyticsCalled,
  analyticsCalled,
  analyticsEventType,
}: Props) => {
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isFetchingLocation = useSelector(({ appData }: ReduxState) => appData.isFetchingLocation);
  const isLoadMoreEnabled = useMonetateDecisionFlag('loadMoreEnabled');
  const [addedCertonaItemObject, setAddedCertonaItemObject] =
    useState<AddCertonaItemObjectType | null>(null);
  const [certonaRecommendationAddedToCart, setCertonaRecommendationAddedToCart] = useState(false);
  const { isMobileFilterOpen } = useProductListView();

  const { data: cmsPLPData, isFetching: isCMSPLPDataFetching } = useContentStackPLPData({
    enabled: true,
  });
  const { renderDealDrawer, handleDealClick } = useDealDrawer();
  const {
    data: searchProductData,
    isSuccess: isSearchProductSuccess,
    isFetching: isSearchProductDataFetching,
  } = useSearchProductData({
    enabled: !viewingPartTypes,
  });
  const { data: searchPartTypesData, isFetching: isSearchPartTypesFetching } = useSearchPartTypes({
    enabled: viewingPartTypes,
    viewingPartTypes,
  });
  const { loadMoreClicked } = useProductList();
  const resultsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // disabling smooth behavior to prevent the user seeing the screen scroll when closing the filter modal
    let timeoutId: NodeJS.Timeout;

    if (isMobileFilterOpen) {
      document.documentElement.classList.add(styles.instantScroll);
    } else if (isMobile) {
      timeoutId = setTimeout(() => {
        document.documentElement.classList.remove(styles.instantScroll);
      }, 100);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        document.documentElement.classList.remove(styles.instantScroll);
      }
    };
  }, [isMobileFilterOpen, isMobile]);

  const handleFilter = useLatestCallback((filter: HandleFilterType) => {
    const { checked, displayName, quickFilter, refinement, selectionType } = filter;
    const { label, properties } = refinement;
    const isQuickFilter = !!quickFilter && !isDesktop;
    const filterName = `${selectionType}: ${displayName}: ${label}`;
    const filterId = properties.filters;

    dispatch(setProductFilterEvents(filterName, filterId, Boolean(checked), isQuickFilter));
  });

  const staticFilterData = viewingPartTypes
    ? searchPartTypesData?.staticNavigation
    : searchProductData?.staticNavigation;

  useEffect(() => {
    return () => {
      dispatch(removeDataFromPage());
    };
  }, [dispatch]);

  useSearchAnalytics({
    analyticsCalled,
    analyticsEventType,
    viewingPartTypes,
    isLoadMoreEnabled,
    isMobile,
    setAnalyticsCalled,
    setViewingPartTypes,
  });

  if (
    (isSearchProductDataFetching ||
      isSearchPartTypesFetching ||
      isFetchingLocation ||
      isCMSPLPDataFetching ||
      searchProductData?.redirectUrl ||
      searchPartTypesData?.redirectLocation) &&
    (isLoadMoreEnabled ? !loadMoreClicked : true)
  ) {
    if (viewingPartTypes) {
      return <TwoColumnSkeleton />;
    }

    return (
      <View padding={[10, 5]}>
        <ShelfSkeleton />
      </View>
    );
  }

  if (viewingPartTypes) {
    return (
      <div data-testid="part-types-view">
        {isMobileFilterOpen && (
          <FilterMobileDrawer
            staticFilterData={
              viewingPartTypes
                ? searchPartTypesData?.staticNavigation ?? []
                : searchProductData?.staticNavigation ?? []
            }
            pageType="searchResults"
            showSpinner={isSearchPartTypesFetching || isSearchProductDataFetching}
            onFilterSelect={handleFilter}
          />
        )}
        <PartTypesView
          viewingPartTypes={viewingPartTypes}
          setViewingPartTypes={setViewingPartTypes}
          setAnalyticsCalled={setAnalyticsCalled}
        />
      </div>
    );
  }

  return (
    <>
      {isMobileFilterOpen && (
        <FilterMobileDrawer
          staticFilterData={
            viewingPartTypes
              ? searchPartTypesData?.staticNavigation ?? []
              : searchProductData?.staticNavigation ?? []
          }
          pageType="searchResults"
          showSpinner={isSearchPartTypesFetching || isSearchProductDataFetching}
          onFilterSelect={handleFilter}
        />
      )}
      <View direction="column" className={styles.container}>
        <View className={styles.header}>
          <CMSShelfResultHeaderModularHandler
            content={cmsPLPData?.shelfresultheader ?? []}
            showSEOData={false}
            setViewingPartTypes={setViewingPartTypes}
            viewingPartTypes={viewingPartTypes}
          />
        </View>
        <View className={styles.leftNavAndResultsContainer}>
          <View className={styles.leftNav}>
            <Filter
              viewingPartTypes={viewingPartTypes}
              pageType="searchleftnav"
              staticFilterData={staticFilterData}
              onFilterSelect={handleFilter}
            />
          </View>
          <View className={styles.results} maxWidth={{ m: '100%' }}>
            <CMSPLPBodyModularHandler
              content={cmsPLPData?.shelfpagebody ?? []}
              pageType="search"
              handleDealClick={handleDealClick}
              onFilterSelect={handleFilter}
              resultsListRef={resultsListRef}
            />
          </View>
        </View>
        <View>
          <CMSResultBottom
            certonaRecommendations={certonaRecommendations}
            setAddedCertonaItemObject={setAddedCertonaItemObject}
            content={cmsPLPData?.shelfresultbottom ?? []}
            onCertonaAddToCartClick={() => setCertonaRecommendationAddedToCart(true)}
          />
        </View>
      </View>
      {isSearchProductSuccess && <SearchResultsMiniCart />}
      <SearchResultsCertonaMiniCart
        addedCertonaItemObject={addedCertonaItemObject}
        setAddedCertonaItemObject={setAddedCertonaItemObject}
        certonaRecommendationAddedToCart={certonaRecommendationAddedToCart}
      />
      {renderDealDrawer()}
    </>
  );
};

export default CMSSearchResultsPage;
